
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../components/docs/ContentSwitcher';
import SampleCode from '../../../components/docs/plaidoms/SampleCode';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'View step by step instructions and sample code you can use to build with the Plaid Assets product.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Create an Asset Report' subtitle='Learn how to create Asset Reports with the Assets product' mdxType="Header" />
    <p>{`In this guide, we'll start from scratch and walk through how to use `}<a parentName="p" {...{
        "href": "/docs/api/products/assets/"
      }}>{`Assets`}</a>{` to generate and retrieve Asset Reports. If a user's Asset Report involves data from multiple financial institutions, the user will need to allow access to each institution, which will in turn enable you to access their data from each institution. If you are already familiar with using Plaid and are set up to make calls to the Plaid API, you can skip ahead to `}<a parentName="p" {...{
        "href": "#creating-asset-reports"
      }}>{`Creating Asset Reports`}</a>{`.`}</p>
    <h4 {...{
      "id": "get-plaid-api-keys-and-complete-application-and-company-profile"
    }}>{`Get Plaid API keys and complete application and company profile`}</h4>
    <p>{`If you don't already have one, you'll need to `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signup"
      }}>{`create a Plaid developer account`}</a>{`. After creating your account, you can find your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`API keys`}</a>{` under Build in the Plaid Dashboard.`}</p>
    <p>{`You will also need to complete your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/app-branding"
      }}>{`application profile`}</a>{` and `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/profile"
      }}>{`company profile`}</a>{` in the Dashboard. This will provide basic information about your app to help users manage their connection on the Plaid Portal at `}<a parentName="p" {...{
        "href": "https://my.plaid.com"
      }}>{`my.plaid.com`}</a>{`. The application profile and company profile must be completed before connecting to certain institutions in Production.`}</p>
    <h4 {...{
      "id": "install-and-initialize-plaid-libraries"
    }}>{`Install and initialize Plaid libraries`}</h4>
    <p>{`You can use our official server-side client libraries to connect to the Plaid API from your application:`}</p>
    <SampleCode.InstallClientLibraries />
    <p>{`After you've installed Plaid's client libraries, you can initialize them by passing in your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, and the environment you wish to connect to (Sandbox or Production). This will make sure the client libraries pass along your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` with each request, and you won't need to explicitly include it in any other calls.`}</p>
    <SampleCode.InitializeClientLibraries />
    <h4 {...{
      "id": "create-an-item-in-link"
    }}>{`Create an Item in Link`}</h4>
    <p>{`Plaid Link is a drop-in module that provides a secure, elegant authentication flow
for each institution that Plaid supports. Link makes it secure and easy for users to
connect their bank accounts to Plaid. Note that these instructions cover Link on the web. For instructions on using Link within mobile apps, see the `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link documentation`}</a>{`.`}</p>
    <p>{`Using Link, we will create a Plaid `}<em parentName="p">{`Item`}</em>{`, which is a Plaid term for a login at a financial institution. An Item is not the same as a financial institution account, although every account will be associated with an Item. For example, if a user has one login at their bank that allows them to access both their checking account and their savings account, a single Item would be associated with both of those accounts. Asset Reports can consist of user data from multiple financial institutions; users will need to use Link to provide access to each financial institution, providing you with multiple Items. If you want to customize Link's look and feel, you can do so from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link"
      }}>{`Dashboard`}</a>{`.`}</p>
    <p>{`Before initializing Link, you will need to create a new `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` on the server side of your application.
A `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` is a short-lived, one-time use token that is used to authenticate your app with Link.
You can create one using the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` endpoint. Then, on the client side of your application, you'll need to initialize Link with the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` that you just created.`}</p>
    <p>{`In the code samples below, you will need to replace `}<inlineCode parentName="p">{`PLAID_CLIENT_ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`PLAID_SECRET`}</inlineCode>{` with your own keys, which you can obtain from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`Dashboard`}</a>{`.`}</p>
    <Callout mdxType="Callout">
      <p>{`Since Asset Reports combine data from all of a user's linked institutions, you can use `}<a parentName="p" {...{
          "href": "/docs/link/multi-item-link"
        }}>{`Multi-Item Link`}</a>{` to let users link multiple accounts in a single Link session.`}</p>
    </Callout>
    <h5 {...{
      "id": "create-a-link_token"
    }}>{`Create a link_token`}</h5>
    <ContentSwitcher.Container id='assets_multi_item' names={['Single institution', 'Multiple institutions (Multi-Item Link)']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <p>{`Since, for Assets, users may need to grant access to more than one financial institution via Link, you may need to initialize Link more than once.`}</p>
        <SampleCode.CreateLinkToken product='assets' />
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <p>{`Multi-Item Link requires a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`. Call `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` first, then pass that `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` (instead of a `}<inlineCode parentName="p">{`user`}</inlineCode>{` object) to `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` along with `}<inlineCode parentName="p">{`enable_multi_item_link: true`}</inlineCode>{`.`}</p>
        <MDXCodeBlock type='server' title='/user/create' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-node",
              "metastring": "#server.js",
              "#server.js": true
            }}>{`app.post('/api/create_plaid_user', async function (request, response) {
  // Get the client_user_id by searching for the current user
  const user = await User.find(...);
  const clientUserId = user.id;
  const userResponse = await client.userCreate({
    client_user_id: clientUserId,
  });
  const plaidUserId = userResponse.data.user_id;
  await User.update(user.id, { plaidUserId });
  response.json({ user_id: plaidUserId });
});
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -X POST https://sandbox.plaid.com/user/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "client_user_id": "\${UNIQUE_USER_ID}"
}'
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby",
              "metastring": "#server.rb",
              "#server.rb": true
            }}>{`post '/api/create_plaid_user' do
  current_user = User.find(...)
  client_user_id = current_user.id

  request = Plaid::UserCreateRequest.new(
    { client_user_id: client_user_id }
  )
  response = client.user_create(request)
  plaid_user_id = response.user_id
  current_user.update(plaid_user_id: plaid_user_id)

  content_type :json
  { user_id: plaid_user_id }.to_json
end
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java",
              "metastring": "#Server.java",
              "#Server.java": true
            }}>{`String clientUserId = userFromDB.id;

UserCreateRequest request = new UserCreateRequest()
  .clientUserId(clientUserId);

UserCreateResponse response = plaidClient
  .userCreate(request, null)
  .execute()
  .body();

String plaidUserId = response.getUserId();
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-python",
              "metastring": "#server.py",
              "#server.py": true
            }}>{`client_user_id = user.id

request = UserCreateRequest(
    client_user_id=client_user_id
)
response = client.user_create(request)
plaid_user_id = response['user_id']
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "#server.go",
              "#server.go": true
            }}>{`clientUserId := user.ID.String()

request := plaid.NewUserCreateRequest(clientUserId)
resp, _, err := client.PlaidApi.UserCreate(ctx).
  UserCreateRequest(*request).
  Execute()
plaidUserId := resp.GetUserId()
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "sdk=fern #server.go",
              "sdk": "fern",
              "#server.go": true
            }}>{`clientUserId := user.ID.String()

request := &plaid.UserCreateRequest{
  ClientUserID: clientUserId,
}
resp, err := client.User.Create(ctx, request)
plaidUserId := resp.GetUserID()
`}</code></pre>
        </MDXCodeBlock>
        <MDXCodeBlock type='server' title='/link/token/create' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-node",
              "metastring": "#server.js",
              "#server.js": true
            }}>{`app.post('/api/create_link_token', async function (request, response) {
  // plaidUserId was returned by /user/create in the previous step
  const user = await User.find(...);
  const plaidUserId = user.plaidUserId;
  const linkTokenRequest = {
    user_id: plaidUserId,
    enable_multi_item_link: true,
    client_name: 'Plaid Test App',
    products: ['assets'],
    language: 'en',
    webhook: 'https://webhook.example.com',
    redirect_uri: 'https://domainname.com/oauth-page.html',
    country_codes: ['US'],
  };
  try {
    const createTokenResponse = await client.linkTokenCreate(linkTokenRequest);
    response.json(createTokenResponse.data);
  } catch (error) {
    // handle error
  }
});
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -X POST https://sandbox.plaid.com/link/token/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "client_name": "Plaid Test App",
  "user_id": "\${PLAID_USER_ID}",
  "enable_multi_item_link": true,
  "products": ["assets"],
  "country_codes": ["US"],
  "language": "en",
  "webhook": "https://webhook.example.com",
  "redirect_uri": "https://domainname.com/oauth-page.html"
}'
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby",
              "metastring": "#server.rb",
              "#server.rb": true
            }}>{`post '/api/create_link_token' do
  # plaid_user_id was returned by /user/create in the previous step
  plaid_user_id = current_user.plaid_user_id

  request = Plaid::LinkTokenCreateRequest.new(
    {
      user_id: plaid_user_id,
      enable_multi_item_link: true,
      client_name: 'Plaid Test App',
      products: ['assets'],
      country_codes: ['US'],
      language: "en",
      redirect_uri: nil_if_empty_envvar('PLAID_REDIRECT_URI'),
      webhook: 'https://webhook.example.com'
    }
  )
  response = client.link_token_create(request)
  content_type :json
  response.to_json
end
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java",
              "metastring": "#Server.java",
              "#Server.java": true
            }}>{`// plaidUserId was returned by /user/create in the previous step
String plaidUserId = userFromDB.plaidUserId;

LinkTokenCreateRequest request = new LinkTokenCreateRequest()
  .userId(plaidUserId)
  .enableMultiItemLink(true)
  .clientName("Plaid Test App")
  .products(Arrays.asList(Products.ASSETS))
  .countryCodes(Arrays.asList(CountryCode.US))
  .language("en")
  .redirectUri("https://domainname.com/oauth-page.html")
  .webhook("https://webhook.example.com");

LinkTokenCreateResponse response = plaidClient
  .linkTokenCreate(request)
  .execute()
  .body();
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-python",
              "metastring": "#server.py",
              "#server.py": true
            }}>{`# plaid_user_id was returned by /user/create in the previous step
plaid_user_id = user.plaid_user_id

request = LinkTokenCreateRequest(
        products=[Products('assets')],
        client_name="Plaid Test App",
        country_codes=[CountryCode('US')],
        redirect_uri='https://domainname.com/oauth-page.html',
        language='en',
        webhook='https://webhook.example.com',
        user_id=plaid_user_id,
        enable_multi_item_link=True,
    )
response = client.link_token_create(request)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "#server.go",
              "#server.go": true
            }}>{`// plaidUserId was returned by /user/create in the previous step
plaidUserId := user.PlaidUserId

request := plaid.NewLinkTokenCreateRequest(
  "Plaid Test App",
  "en",
  []plaid.CountryCode{plaid.COUNTRYCODE_US},
)
request.SetUserId(plaidUserId)
request.SetEnableMultiItemLink(true)
request.SetWebhook("https://webhook.example.com")
request.SetRedirectUri("https://domainname.com/oauth-page.html")
request.SetProducts([]plaid.Products{plaid.PRODUCTS_ASSETS})

resp, _, err := client.PlaidApi.LinkTokenCreate(ctx).
  LinkTokenCreateRequest(*request).
  Execute()
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "sdk=fern #server.go",
              "sdk": "fern",
              "#server.go": true
            }}>{`// plaidUserId was returned by /user/create in the previous step
plaidUserId := user.PlaidUserId

request := &plaid.LinkTokenCreateRequest{
  ClientName: "Plaid Test App",
  Language: "en",
  CountryCodes: []plaid.CountryCode{plaid.CountryCodeUs},
  UserID: plaid.String(plaidUserId),
  EnableMultiItemLink: plaid.Bool(true),
  Webhook: plaid.String("https://webhook.example.com"),
  RedirectURI: plaid.String("https://domainname.com/oauth-page.html"),
  Products: []plaid.Products{plaid.ProductsAssets},
}

resp, err := client.Link.CreateToken(ctx, request)
`}</code></pre>
        </MDXCodeBlock>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h5 {...{
      "id": "install-link-dependency"
    }}>{`Install Link dependency`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "#index.html",
        "#index.html": true
      }}>{`<head>
  <title>Connect a bank</title>
  <script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
</head>
`}</code></pre>
    <h5 {...{
      "id": "configure-the-client-side-link-handler"
    }}>{`Configure the client-side Link handler`}</h5>
    <ContentSwitcher.Container id='assets_multi_item' names={['Single institution', 'Multiple institutions (Multi-Item Link)']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false #app.js",
            "switcher": "false",
            "#app.js": true
          }}>{`const linkHandler = Plaid.create({
  token: (await $.post('/create_link_token')).link_token,
  onSuccess: (public_token, metadata) => {
    // Send the public_token to your app server.
    $.post('/exchange_public_token', {
      public_token: public_token,
    });
  },
  onExit: (err, metadata) => {
    // Optionally capture when your user exited the Link flow.
    // Storing this information can be helpful for support.
  },
  onEvent: (eventName, metadata) => {
    // Optionally capture Link flow events, streamed through
    // this callback as your users connect an Item to Plaid.
  },
});

linkHandler.open();
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <p>{`With Multi-Item Link, `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` will be called but will not contain public tokens. Instead, get them either from the `}<a parentName="p" {...{
            "href": "/docs/api/link/#session_finished"
          }}><inlineCode parentName="a">{`SESSION_FINISHED`}</inlineCode></a>{` webhook or by calling `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` after `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` fires.`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false #app.js",
            "switcher": "false",
            "#app.js": true
          }}>{`const linkHandler = Plaid.create({
  token: (await $.post('/create_link_token')).link_token,
  onSuccess: (public_token, metadata) => {
    // public_token will not be populated here; public_tokens are
    // retrieved via the SESSION_FINISHED webhook or /link/token/get instead.
  },
  onExit: (err, metadata) => {
    // Optionally capture when your user exited the Link flow.
    // Storing this information can be helpful for support.
  },
  onEvent: (eventName, metadata) => {
    // Optionally capture Link flow events, streamed through
    // this callback as your users connect an Item to Plaid.
  },
});

linkHandler.open();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node",
            "metastring": "switcher=false #webhookServer.js",
            "switcher": "false",
            "#webhookServer.js": true
          }}>{`app.post('/server/receive_webhook', async (req, res, next) => {
  const { webhook_type, webhook_code, status, public_tokens } = req.body;
  const isMultiItemSessionFinished =
    webhook_type === 'LINK' &&
    webhook_code === 'SESSION_FINISHED' &&
    status === 'success';
  if (isMultiItemSessionFinished) {
    // Exchange each token in public_tokens for an access_token
    // via /item/public_token/exchange.
  }
  // Handle other types of webhooks
});
`}</code></pre>
        <p>{`Alternatively, call `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` with the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` and read the public tokens from `}<inlineCode parentName="p">{`link_sessions[].results.item_add_results[]`}</inlineCode>{` in the response.`}</p>
        <MDXCodeBlock type='server' title='/link/token/get' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-node",
              "metastring": "#server.js",
              "#server.js": true
            }}>{`const request = {
  link_token: linkToken,
};
const response = await client.linkTokenGet(request);
const publicTokens = response.data.link_sessions
  .flatMap((session) => session.results.item_add_results)
  .map((result) => result.public_token);
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -X POST https://sandbox.plaid.com/link/token/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "link_token": "\${LINK_TOKEN}"
}'
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby",
              "metastring": "#server.rb",
              "#server.rb": true
            }}>{`request = Plaid::LinkTokenGetRequest.new({ link_token: link_token })
response = client.link_token_get(request)
public_tokens = response.link_sessions.flat_map do |session|
  session.results.item_add_results.map(&:public_token)
end
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java",
              "metastring": "#Server.java",
              "#Server.java": true
            }}>{`LinkTokenGetRequest request = new LinkTokenGetRequest()
  .linkToken(linkToken);
LinkTokenGetResponse response = plaidClient
  .linkTokenGet(request)
  .execute()
  .body();

List<String> publicTokens = new ArrayList<>();
for (LinkTokenGetSessionsResponse session : response.getLinkSessions()) {
  LinkSessionResults results = session.getResults();
  for (LinkSessionItemAddResult result : results.getItemAddResults()) {
    publicTokens.add(result.getPublicToken());
  }
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-python",
              "metastring": "#server.py",
              "#server.py": true
            }}>{`request = LinkTokenGetRequest(link_token=link_token)
response = client.link_token_get(request)
public_tokens = [
    result['public_token']
    for session in response['link_sessions']
    for result in session['results']['item_add_results']
]
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "#server.go",
              "#server.go": true
            }}>{`linkTokenGetRequest := plaid.NewLinkTokenGetRequest(linkToken)
linkTokenGetResp, _, err := client.PlaidApi.LinkTokenGet(ctx).
  LinkTokenGetRequest(*linkTokenGetRequest).
  Execute()

var publicTokens []string
for _, session := range linkTokenGetResp.GetLinkSessions() {
  results := session.GetResults()
  for _, result := range results.GetItemAddResults() {
    publicTokens = append(publicTokens, result.GetPublicToken())
  }
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "sdk=fern #server.go",
              "sdk": "fern",
              "#server.go": true
            }}>{`linkTokenGetRequest := &plaid.LinkTokenGetRequest{
  LinkToken: linkToken,
}
linkTokenGetResp, err := client.Link.GetToken(ctx, linkTokenGetRequest)

var publicTokens []string
for _, session := range linkTokenGetResp.GetLinkSessions() {
  results := session.GetResults()
  for _, result := range results.GetItemAddResults() {
    publicTokens = append(publicTokens, result.GetPublicToken())
  }
}
`}</code></pre>
        </MDXCodeBlock>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h4 {...{
      "id": "get-a-persistent-access_token"
    }}>{`Get a persistent access_token`}</h4>
    <p>{`Next, on the server side, we need to exchange our `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`(s) for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` for each Item the user provided you with via Link. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` will allow us to make authenticated calls to the Plaid API for its corresponding financial institution. Doing so is as easy as calling the `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` endpoint from our server-side handler. We'll use the client library we configured earlier to make the API call.`}</p>
    <p>{`Save each `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` in a secure datastore, as they're used to access `}<inlineCode parentName="p">{`Item`}</inlineCode>{` data and identify `}<inlineCode parentName="p">{`webhooks`}</inlineCode>{`, respectively. An `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` will remain valid unless you actively choose to expire it via rotation or remove the corresponding Item via `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{`. An `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` should be stored securely and never in client-side code. A `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` is a one-time use token with a lifetime of 30 minutes, so there is no need to store it.`}</p>
    <SampleCode.ExchangePublicToken />
    <p>{`Call `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` once per token you received, saving the resulting `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` pair each time.`}</p>
    <h4 {...{
      "id": "creating-asset-reports"
    }}>{`Creating Asset Reports`}</h4>
    <p>{`Now that the authentication step is out of the way, we can begin using authenticated endpoints from the Plaid API and create an Asset Report using the `}<inlineCode parentName="p">{`/asset_report/create`}</inlineCode>{` endpoint.`}</p>
    <h5 {...{
      "id": "assets-sample-request-create"
    }}>{`Assets sample request: create`}</h5>
    <MDXCodeBlock title='/asset_report/create' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import plaid
from plaid.model.asset_report_create_request import AssetReportCreateRequest
from plaid.model.asset_report_create_request_options import AssetReportCreateRequestOptions
from plaid.model.asset_report_user import AssetReportUser

# access_tokens is a list of Item access tokens.
# Note that the assets product must be enabled for all Items.
# All fields on the options object are optional.
request = AssetReportCreateRequest(
    access_tokens=[access_token],
    days_requested=90,
    options=AssetReportCreateRequestOptions(
        webhook='https://www.example.com',
        client_report_id='123',
        user=AssetReportUser(
            client_user_id='789',
            first_name='Jane',
            middle_name='Leah',
            last_name='Doe',
            ssn='123-45-6789',
            phone_number='(555) 123-4567',
            email='jane.doe@example.com',
        )
    )
)
response = client.asset_report_create(request)
asset_report_id = response['asset_report_id']
asset_report_token = response['asset_report_token']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`require 'plaid'

# access_tokens is a list of Item access tokens.
# Note that the assets product must be enabled for all Items.
# All fields on the options Hash are optional.
options = {
  client_report_id: '123',
  webhook: 'https://www.example.com',
  user: {
    client_user_id: '789',
    first_name: 'Jane',
    middle_name: 'Leah',
    last_name: 'Doe',
    ssn: '123-45-6789',
    phone_number: '(555) 123-4567',
    email: 'jane.doe@example.com',
  },
}
request = Plaid::AssetReportCreateRequest.new(
  {
    access_tokens: [access_token],
    days_requested: 90,
    options: options
  }
)
response = client.asset_report_create(request)
asset_report_id = response.asset_report_id
asset_report_token = response.asset_report_token
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`import (
    "context"

    "github.com/plaid/plaid-go/v48/plaid"
)

request := plaid.NewAssetReportCreateRequest(90)
request.SetAccessTokens([]string{accessToken})
response, _, err := client.PlaidApi.AssetReportCreate(ctx).AssetReportCreateRequest(
  *request,
).Execute()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`import (
    "context"

    "github.com/plaid/plaid-go/v49"
)

request := &plaid.AssetReportCreateRequest{DaysRequested: 90}
request.AccessTokens = []string{accessToken}
response, err := client.AssetReport.Create(ctx, request)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/asset_report/create \\
-H 'Content-Type: application/json' \\
-d '{
 "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
 "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
 "access_tokens": [String],
 "days_requested": Integer,
 "options": {
    // all optional
    "client_report_id": String,
    "webhook": String,
    "user": {
      // all optional
      "client_user_id": String,
      "first_name": String,
      "middle_name": String,
      "last_name": String,
      "ssn": String,
      "phone_number": String,
      "email": String
    }
 }
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`import com.plaid.client.model.AssetReportCreateRequest;
import com.plaid.client.model.AssetReportCreateRequestOptions;
import com.plaid.client.model.AssetReportUser;
import com.plaid.client.model.AssetReportCreateResponse;

// access_tokens is an array of Item access tokens.
// Note that the assets product must be enabled for all Items.
// All fields on the options object are optional.
AssetReportCreateRequest assetReportCreateRequest = new AssetReportCreateRequest()
  .accessTokens(access_tokens)
  .daysRequested(90);

AssetReportUser assetReportUser = new AssetReportUser()
  .firstName("Alberta")
  .middleName("Bobbeth")
  .lastName("Charleson");

AssetReportCreateRequestOptions assetReportCreateOptions = new AssetReportCreateRequestOptions()
  .user(assetReportUser)
  .webhook(webhookUrl);

assetReportCreateRequest.options(assetReportCreateOptions);

Response<AssetReportCreateResponse> response = client
  .assetReportCreate(assetReportCreateRequest)
  .execute();
String assetReportId = response.body().getAssetReportId();
String assetReportToken = response.body().getAssetReportToken();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const { AssetReportCreateRequest } = require('plaid');

const daysRequested = 90;
const options = {
  client_report_id: '123',
  webhook: 'https://www.example.com',
  user: {
    client_user_id: '789',
    first_name: 'Jane',
    middle_name: 'Leah',
    last_name: 'Doe',
    ssn: '123-45-6789',
    phone_number: '(555) 123-4567',
    email: 'jane.doe@example.com',
  },
};
const request: AssetReportCreateRequest = {
  access_tokens: [accessToken],
  days_requested: daysRequested,
  options,
};
// accessTokens is an array of Item access tokens.
// Note that the assets product must be enabled for all Items.
// All fields on the options object are optional.
try {
  const response = await plaidClient.assetReportCreate(request);
  const assetReportId = response.data.asset_report_id;
  const assetReportToken = response.data.asset_report_token;
} catch (error) {
  // handle error
}
`}</code></pre>
    </MDXCodeBlock>
    <p>{`Sample response data is below.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Assets response: create",
        "#Assets": true,
        "response:": true,
        "create": true
      }}>{`{
  "asset_report_token": "assets-sandbox-6f12f5bb-22dd-4855-b918-f47ec439198a",
  "asset_report_id": "1f414183-220c-44f5-b0c8-bc0e6d4053bb",
  "request_id": "Iam3b"
}
`}</code></pre>
    <h4 {...{
      "id": "fetching-asset-data"
    }}>{`Fetching asset data`}</h4>
    <p>{`Once an Asset Report has been created, it can be retrieved to analyze the user's loan eligibility. For more detailed information on the schema for Asset Reports, see `}<inlineCode parentName="p">{`/asset_report/get`}</inlineCode>{`.`}</p>
    <p>{`Asset Reports are not generated instantly. If you receive a `}<inlineCode parentName="p">{`PRODUCT_NOT_READY`}</inlineCode>{` error when calling `}<inlineCode parentName="p">{`/asset_report/get`}</inlineCode>{`, the requested Asset Report has not yet been generated. To be alerted when the requested Asset Report has been generated, listen to `}<a parentName="p" {...{
        "href": "/docs/api/products/assets/#product_ready"
      }}>{`Assets webhooks`}</a>{`.`}</p>
    <h5 {...{
      "id": "assets-sample-request-get"
    }}>{`Assets sample request: get`}</h5>
    <MDXCodeBlock title='/asset_report/get' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`import (
    "context"

    "github.com/plaid/plaid-go/v48/plaid"
)

request := plaid.NewAssetReportGetRequest()
request.SetAssetReportToken(assetReportToken)
response, _, err := client.PlaidApi.AssetReportGet(ctx).AssetReportGetRequest(*request).Execute()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`import (
    "context"

    "github.com/plaid/plaid-go/v49"
)

request := &plaid.AssetReportGetRequest{}
request.AssetReportToken = &assetReportToken
response, err := client.AssetReport.Get(ctx, request)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/asset_report/get \\
 -H 'Content-Type: application/json' \\
 -d '{
   "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
   "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
   "asset_report_token": String,
   "include_insights": Boolean
 }'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import json
import plaid
from plaid.model.asset_report_get_request import AssetReportGetRequest

# asset_report_token is the token from the AssetReport.create response.
try:
    request = AssetReportGetRequest(asset_report_token=asset_report_token)
    response = client.asset_report_get(request)
    report = response['report']
except plaid.ApiException as e:
   if json.loads(e.body)['error_code'] == 'PRODUCT_NOT_READY':
       # Asset report is not ready yet. Try again later.
       pass
   else:
       # Handle error.
       pass
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`import com.plaid.client.model.AssetReportGetRequest;
import com.plaid.client.model.AssetReportGetResponse;

// assetReportToken is the token from the AssetReportCreateRequest response.
AssetReportGetRequest request = new AssetReportGetRequest()
    .includeInsights(true)
    .assetReportToken(assetReportToken);

Response<AssetReportGetResponse> response =
    client().assetReportGet(request).execute();
AssetReport report = response.body().getReport();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`require 'plaid'

# asset_report_token is the token from the AssetReport.create response.
request = Plaid::AssetReportGetRequest.new({ asset_report_token: asset_report_token })
response = client.asset_report_get(request)
report = response.report
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const { AssetReportGetRequest } = require('plaid');

const request: AssetReportGetRequest = {
  asset_report_token: assetReportToken,
  include_insights: true,
};
try {
  const response = await plaidClient.assetReportGet(request);
  const assetReportId = response.data.report.asset_report_id;
} catch (error) {
  // handle error
}
`}</code></pre>
    </MDXCodeBlock>
    <p>{`Sample response data is below.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Assets response: get",
        "#Assets": true,
        "response:": true,
        "get": true
      }}>{`{
  "report": {
    "asset_report_id": "bf3a0490-344c-4620-a219-2693162e4b1d",
    "client_report_id": "123abc",
    "date_generated": "2020-06-05T22:47:53Z",
    "days_requested": 2,
    "items": [
      {
        "accounts": [
          {
            "account_id": "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
            "balances": {
              "available": 200,
              "current": 210,
              "iso_currency_code": "USD",
              "limit": null,
              "unofficial_currency_code": null
            },
            "days_available": 2,
            "historical_balances": [
              {
                "current": 210,
                "date": "2020-06-04",
                "iso_currency_code": "USD",
                "unofficial_currency_code": null
              },
              {
                "current": 210,
                "date": "2020-06-03",
                "iso_currency_code": "USD",
                "unofficial_currency_code": null
              }
            ],
            "mask": "1111",
            "name": "Plaid Saving",
            "official_name": "Plaid Silver Standard 0.1% Interest Saving",
            "owners": [
              {
                "addresses": [
                  {
                    "data": {
                      "city": "Malakoff",
                      "country": "US",
                      "postal_code": "14236",
                      "region": "NY",
                      "street": "2992 Cameron Road"
                    },
                    "primary": true
                  },
                  {
                    "data": {
                      "city": "San Matias",
                      "country": "US",
                      "postal_code": "93405-2255",
                      "region": "CA",
                      "street": "2493 Leisure Lane"
                    },
                    "primary": false
                  }
                ],
                "emails": [
                  {
                    "data": "accountholder0@example.com",
                    "primary": true,
                    "type": "primary"
                  },
                  {
                    "data": "extraordinarily.long.email.username.123456@reallylonghostname.com",
                    "primary": false,
                    "type": "other"
                  }
                ],
                "names": ["Alberta Bobbeth Charleson"],
                "phone_numbers": [
                  {
                    "data": "1112223333",
                    "primary": false,
                    "type": "home"
                  },
                  {
                    "data": "1112225555",
                    "primary": false,
                    "type": "mobile1"
                  }
                ]
              }
            ],
            "ownership_type": null,
            "subtype": "savings",
            "transactions": [],
            "type": "depository"
          },
          {
            "account_id": "BxBXxLj1m4HMXBm9WZJyUg9XLd4rKEhw8Pb1J",
            "balances": {
              "available": null,
              "current": 56302.06,
              "iso_currency_code": "USD",
              "limit": null,
              "unofficial_currency_code": null
            },
            "days_available": 2,
            "historical_balances": [],
            "mask": "8888",
            "name": "Plaid Mortgage",
            "official_name": null,
            "owners": [
              {
                "addresses": [
                  {
                    "data": {
                      "city": "Malakoff",
                      "country": "US",
                      "postal_code": "14236",
                      "region": "NY",
                      "street": "2992 Cameron Road"
                    },
                    "primary": true
                  },
                  {
                    "data": {
                      "city": "San Matias",
                      "country": "US",
                      "postal_code": "93405-2255",
                      "region": "CA",
                      "street": "2493 Leisure Lane"
                    },
                    "primary": false
                  }
                ],
                "emails": [
                  {
                    "data": "accountholder0@example.com",
                    "primary": true,
                    "type": "primary"
                  },
                  {
                    "data": "extraordinarily.long.email.username.123456@reallylonghostname.com",
                    "primary": false,
                    "type": "other"
                  }
                ],
                "names": ["Alberta Bobbeth Charleson"],
                "phone_numbers": [
                  {
                    "data": "1112223333",
                    "primary": false,
                    "type": "home"
                  },
                  {
                    "data": "1112225555",
                    "primary": false,
                    "type": "mobile1"
                  }
                ]
              }
            ],
            "ownership_type": null,
            "subtype": "mortgage",
            "transactions": [],
            "type": "loan"
          },
          {
            "account_id": "dVzbVMLjrxTnLjX4G66XUp5GLklm4oiZy88yK",
            "balances": {
              "available": null,
              "current": 410,
              "iso_currency_code": "USD",
              "limit": null,
              "unofficial_currency_code": null
            },
            "days_available": 2,
            "historical_balances": [
              {
                "current": 410,
                "date": "2020-06-04",
                "iso_currency_code": "USD",
                "unofficial_currency_code": null
              },
              {
                "current": 410,
                "date": "2020-06-03",
                "iso_currency_code": "USD",
                "unofficial_currency_code": null
              }
            ],
            "mask": "3333",
            "name": "Plaid Credit Card",
            "official_name": "Plaid Diamond 12.5% APR Interest Credit Card",
            "owners": [
              {
                "addresses": [
                  {
                    "data": {
                      "city": "Malakoff",
                      "country": "US",
                      "postal_code": "14236",
                      "region": "NY",
                      "street": "2992 Cameron Road"
                    },
                    "primary": true
                  },
                  {
                    "data": {
                      "city": "San Matias",
                      "country": "US",
                      "postal_code": "93405-2255",
                      "region": "CA",
                      "street": "2493 Leisure Lane"
                    },
                    "primary": false
                  }
                ],
                "emails": [
                  {
                    "data": "accountholder0@example.com",
                    "primary": true,
                    "type": "primary"
                  },
                  {
                    "data": "extraordinarily.long.email.username.123456@reallylonghostname.com",
                    "primary": false,
                    "type": "other"
                  }
                ],
                "names": ["Alberta Bobbeth Charleson"],
                "phone_numbers": [
                  {
                    "data": "1112223333",
                    "primary": false,
                    "type": "home"
                  },
                  {
                    "data": "1112225555",
                    "primary": false,
                    "type": "mobile1"
                  }
                ]
              }
            ],
            "ownership_type": null,
            "subtype": "credit card",
            "transactions": [],
            "type": "credit"
          }
        ],
        "date_last_updated": "2020-06-05T22:47:52Z",
        "institution_id": "ins_3",
        "institution_name": "Chase",
        "item_id": "eVBnVMp7zdTJLkRNr33Rs6zr7KNJqBFL9DrE6"
      }
    ],
    "user": {
      "client_user_id": "123456789",
      "email": "accountholder0@example.com",
      "first_name": "Alberta",
      "last_name": "Charleson",
      "middle_name": "Bobbeth",
      "phone_number": "111-222-3333",
      "ssn": "123-45-6789"
    }
  },
  "request_id": "eYupqX1mZkEuQRx",
  "warnings": []
}
`}</code></pre>
    <h4 {...{
      "id": "working-with-assets-data"
    }}>{`Working with Assets data`}</h4>
    <p>{`After your initial `}<inlineCode parentName="p">{`/asset_report/create`}</inlineCode>{` and `}<inlineCode parentName="p">{`/asset_report/get`}</inlineCode>{` requests, you may want your application to fetch updated Asset Reports, provide Audit Copies of Asset Reports, and more. Consult the `}<a parentName="p" {...{
        "href": "/docs/api/products/assets"
      }}>{`API Reference`}</a>{` to explore these and other options.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "get-plaid-api-keys-and-complete-application-and-company-profile",
        level: 4,
        title: "Get Plaid API keys and complete application and company profile",
      },
{
        id: "install-and-initialize-plaid-libraries",
        level: 4,
        title: "Install and initialize Plaid libraries",
      },
{
        id: "create-an-item-in-link",
        level: 4,
        title: "Create an Item in Link",
      },
{
        id: "get-a-persistent-access_token",
        level: 4,
        title: "Get a persistent access_token",
      },
{
        id: "creating-asset-reports",
        level: 4,
        title: "Creating Asset Reports",
      },
{
        id: "fetching-asset-data",
        level: 4,
        title: "Fetching asset data",
      },
{
        id: "working-with-assets-data",
        level: 4,
        title: "Working with Assets data",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
